"use client";

import { DatePicker } from "@/components/ui/date-picker";
import PageHeader from "@/components/layout/PageHeader";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { get, post } from "../../utils/service";
import { parseApiError, today, formatDate } from "@/utils/finance";
import { btnPrimary, btnGhost, inputCls, labelCls } from "@/components/finance/Modal";

interface Contract { id: string; service_type: string; value: string | null; currency: string; start_date: string }

const addDays = (d: string, n: number) => { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

export default function InvoiceNewPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [clientName, setClientName] = useState("");
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    contract_id: "", description: "", amount: "", currency: "USD", issued_date: today(), due_date: addDays(today(), 14),
    period_start: "", period_end: "", notes: "",
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    get<{ data: { client: { company_name: string } } }>(`/clients/${clientId}/invoice-summary`).then((r) => setClientName(r.data.client.company_name)).catch(() => navigate("/invoices"));
    get<{ data: { contracts: Contract[] } }>(`/clients/${clientId}/contracts`).then((r) => setContracts(r.data.contracts)).catch(() => undefined);
  }, [clientId, navigate]);

  const pickContract = (id: string) => {
    const c = contracts.find((x) => x.id === id);
    setForm((f) => ({ ...f, contract_id: id, ...(c ? { currency: c.currency, description: f.description || c.service_type.replace(/_/g, " "), amount: f.amount || (c.value ?? "") } : {}) }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setFieldErrors({});
    try {
      const body: Record<string, string> = { client_id: clientId!, ...form };
      Object.keys(body).forEach((k) => body[k] === "" && delete body[k]);
      const r = await post<{ data: { invoice: { id: string } } }>("/invoices", body);
      navigate(`/invoices/${r.data.invoice.id}`);
    } catch (err) {
      const f = parseApiError(err);
      setError(f.message);
      setFieldErrors(f.fieldErrors);
    } finally {
      setBusy(false);
    }
  };

  const err = (k: string) => fieldErrors[k] && <p className="mt-1 text-xs text-red-600">{fieldErrors[k]}</p>;

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="New invoice" description={clientName ? `For ${clientName}` : "Create an invoice for this client"}>
          <Link to={`/invoices/client/${clientId}`} className={btnGhost}>Cancel</Link>
        </PageHeader>

        <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
          <div>
            <label className={labelCls}>Contract (optional)</label>
            <select className={inputCls} value={form.contract_id} onChange={(e) => pickContract(e.target.value)}>
              <option value="">One-off invoice (no contract)</option>
              {contracts.map((c) => <option key={c.id} value={c.id}>{c.service_type.replace(/_/g, " ")} — effective {formatDate(c.start_date)}</option>)}
            </select>
            {err("contract_id")}
            <p className="mt-1 text-[11px] text-gray-500">For recurring, period-based invoices use "Generate missing invoices" on the client page — it prevents duplicates.</p>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <input className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} required />
            {err("description")}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Amount</label><input className={inputCls} type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} required />{err("amount")}</div>
            <div><label className={labelCls}>Currency</label><input className={inputCls} maxLength={3} value={form.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} required />{err("currency")}</div>
            <div><label className={labelCls}>Invoice date</label><DatePicker value={form.issued_date} onChange={(v) => set("issued_date", v)} required />{err("issued_date")}</div>
            <div><label className={labelCls}>Due date</label><DatePicker value={form.due_date} onChange={(v) => set("due_date", v)} min={form.issued_date} required />{err("due_date")}</div>
            <div><label className={labelCls}>Service period start</label><DatePicker value={form.period_start} onChange={(v) => set("period_start", v)} clearable />{err("period_start")}</div>
            <div><label className={labelCls}>Service period end</label><DatePicker value={form.period_end} onChange={(v) => set("period_end", v)} min={form.period_start || undefined} clearable />{err("period_end")}</div>
          </div>
          <div><label className={labelCls}>Notes</label><textarea className={inputCls} rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end"><button className={btnPrimary} disabled={busy}>{busy ? "Creating…" : "Create invoice"}</button></div>
        </form>
      </div>
    </div>
  );
}
